import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { FaArrowLeft, FaEnvelope, FaRedo } from 'react-icons/fa';
import * as PATH from '../../../CentralisedLink';
import {
  checkEmailMigrationStatus,
  resendEmailChange,
} from '../../../Redux/Dashboard/External/EmailMigration/EmailMigrationAction';
import { PENDING_HELIX_EMAIL_FALLBACK, PENDING_HELIX_EMAIL_KEY } from '../../../Constants/emailMigration';

const RESEND_COOLDOWN_SECONDS = 10;
const STATUS_POLL_INTERVAL_MS = 5 * 60 * 1000;

const EmailVerificationSent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const {
    resendLoading,
    statusLoading,
    migrationCompleted,
  } = useSelector((state) => state.EmailMigration);
  const storedPendingEmail = sessionStorage.getItem(PENDING_HELIX_EMAIL_KEY);
  const pendingEmail = storedPendingEmail || PENDING_HELIX_EMAIL_FALLBACK;
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const cooldownIntervalRef = useRef(null);
  const statusLoadingRef = useRef(statusLoading);

  useEffect(() => {
    statusLoadingRef.current = statusLoading;
  }, [statusLoading]);

  useEffect(() => {
    const runStatusCheck = async () => {
      if (statusLoadingRef.current) {
        return;
      }

      const result = await dispatch(checkEmailMigrationStatus());
      if (result.success && result.data?.migrationCompleted) {
        navigate(PATH.EMAIL_UPDATED_PATH);
      }
    };

    runStatusCheck();
    const statusInterval = window.setInterval(runStatusCheck, STATUS_POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(statusInterval);
    };
  }, [dispatch, navigate]);

  useEffect(() => {
    if (migrationCompleted === true) {
      navigate(PATH.EMAIL_UPDATED_PATH);
    }
  }, [migrationCompleted, navigate]);

  useEffect(() => {
    return () => {
      if (cooldownIntervalRef.current) {
        window.clearInterval(cooldownIntervalRef.current);
      }
    };
  }, []);

  const startResendCooldown = () => {
    if (cooldownIntervalRef.current) {
      window.clearInterval(cooldownIntervalRef.current);
    }

    setCooldownRemaining(RESEND_COOLDOWN_SECONDS);
    cooldownIntervalRef.current = window.setInterval(() => {
      setCooldownRemaining((remaining) => {
        if (remaining <= 1) {
          window.clearInterval(cooldownIntervalRef.current);
          cooldownIntervalRef.current = null;
          return 0;
        }

        return remaining - 1;
      });
    }, 1000);
  };

  const handleResendEmail = async () => {
    if (resendLoading || cooldownRemaining > 0) {
      return;
    }

    if (!storedPendingEmail) {
      enqueueSnackbar('Please start again from Update Email.', { variant: 'error' });
      navigate(PATH.UPDATE_EMAIL_PATH);
      return;
    }

    startResendCooldown();
    const result = await dispatch(resendEmailChange(storedPendingEmail));

    if (result.success) {
      enqueueSnackbar('Verification email resent.', { variant: 'success' });
      return;
    }

    enqueueSnackbar(result.error || 'Something went wrong. Please try again.', { variant: 'error' });
  };

  const handleBackToUpdateEmail = () => {
    navigate(PATH.UPDATE_EMAIL_PATH);
  };

  return (
    <main className="update-email-page email-verification-sent-page">
      <section className="update-email-panel" aria-labelledby="email-verification-sent-title">
        <div className="update-email-hero-icon" aria-hidden="true">
          <FaEnvelope />
        </div>
        <h1 id="email-verification-sent-title" className="update-email-title">
          Verification Email Sent
        </h1>
        <p className="update-email-subtitle">
          We've sent a verification link to
          <span className="email-verification-sent-email">{pendingEmail}</span>
        </p>

        <div className="email-verification-sent-card">
          <div className="email-verification-steps">
            <h2 className="email-verification-steps-title">What's next?</h2>

            <ol className="email-verification-step-list">
              <li className="email-verification-step">
                <span className="email-verification-step-number">1</span>
                <span>Check your inbox at {pendingEmail}</span>
              </li>
              <li className="email-verification-step">
                <span className="email-verification-step-number">2</span>
                <span>Click the verification link in the email</span>
              </li>
              <li className="email-verification-step">
                <span className="email-verification-step-number">3</span>
                <span>Your email will be updated automatically</span>
              </li>
            </ol>
          </div>

          <p className="email-verification-helper">
            Didn't receive the email? Check your spam folder or request a new verification email.
          </p>
        </div>

        <div className="email-verification-actions" aria-label="Email verification actions">
          <button
            type="button"
            className="email-verification-action email-verification-action--secondary"
            onClick={handleResendEmail}
            disabled={resendLoading || cooldownRemaining > 0}
          >
            <FaRedo aria-hidden="true" />
            {resendLoading
              ? 'Resending...'
              : cooldownRemaining > 0
                ? `Resend Email (${cooldownRemaining}s)`
                : 'Resend Email'}
          </button>
          <button
            type="button"
            className="email-verification-action email-verification-action--primary"
            onClick={handleBackToUpdateEmail}
          >
            <FaArrowLeft aria-hidden="true" />
            Back to Update Email
          </button>
        </div>
      </section>
    </main>
  );
};

export default EmailVerificationSent;
